import { z } from "zod";
import { PublicIdSchema } from "./common";

export const DeliveryPlanStatus = z.enum([
  "draft",
  "optimized",
  "confirmed",
  "in_progress",
  "completed",
]);

/**
 * 配送計画。#425 で日単位 (1 日 1 plan) に再定義。便 (time_slot) は plan の属性ではなく、
 * 注文の時間帯分類と画面の表示フィルタとして扱う。
 */
export const DeliveryPlan = z.object({
  id: PublicIdSchema,
  plan_date: z.iso.date(),
  status: DeliveryPlanStatus,
  total_routes: z.number().int().min(0),
  total_deliveries: z.number().int().min(0),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

/** 日付 upsert (#425)。同一日の plan が既にあればそれを返す (重複 plan を作らない)。 */
export const CreateDeliveryPlanInput = z.object({
  plan_date: z.iso.date(),
});

export const ListDeliveryPlansInput = z.object({
  plan_date: z.iso.date().optional(),
  status: DeliveryPlanStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
});

/**
 * 配送計画確定 (API-26) のレスポンス.
 * 全ルートが assigned 済みであることを検証し、計画とルートを confirmed に遷移、
 * 確定結果を返す。計画確定そのものでは通知しない.
 */
export const ConfirmDeliveryPlanResult = z.object({
  plan_id: PublicIdSchema,
  status: DeliveryPlanStatus,
  /**
   * #536: 確定できず据え置いたレーン。1 本の未割当で当日全体の確定を止めず、
   * 確定できるレーンだけ確定して、残りを呼び出し側へ返す (画面は警告に使う)。
   * 確定できるレーンが 1 本も無い場合は従来どおり 422 (DRIVER_NOT_ASSIGNED /
   * VEHICLE_NOT_ASSIGNED) を返すため、ここが空でないのは「一部確定できた」ケース。
   */
  skipped_unassigned_route_ids: z.array(PublicIdSchema).default([]),
  skipped_vehicle_unassigned_route_ids: z.array(PublicIdSchema).default([]),
});

/**
 * 配車確定の全体解除 (plan.unconfirm) のレスポンス.
 * confirmed のレーンだけを assigned に戻し、started/completed のレーンはスキップして明示する.
 * plan.status は配下レーンから導出し直す (未確定レーンが 1 本でもあれば optimized).
 */
export const UnconfirmDeliveryPlanResult = z.object({
  plan_id: PublicIdSchema,
  status: DeliveryPlanStatus,
  /** assigned に戻したレーン (route) の public_id */
  unconfirmed_route_ids: z.array(PublicIdSchema),
  /** started/completed で解除対象外だったレーン (route) の public_id */
  skipped_route_ids: z.array(PublicIdSchema),
  notifications_sent: z.number().int().min(0),
});

export type DeliveryPlanDto = z.infer<typeof DeliveryPlan>;
export type ConfirmDeliveryPlanResultDto = z.infer<typeof ConfirmDeliveryPlanResult>;
export type UnconfirmDeliveryPlanResultDto = z.infer<typeof UnconfirmDeliveryPlanResult>;
